import {
  IAuditLogListResult,
  IBillingDetails,
  IBillingDetailsListResult,
  IExceptionList,
  IExceptionListsResult,
  IObeDetails,
  IObeEventLogResult,
  IObeListResult,
  IEfcContextMarksListResult,
  IPaymentClaim,
  IPaymentClaimsListResult,
  ITollDeclaration,
  ITollDeclarationsListResult,
  ITollingAdminAccessObject,
  IEfcContextMark,
  ITollChargersResponse,
  ITollCharger,
  IContractIssuerListResult,
  IContractIssuerList,
  IAgreementLine,
  IAgreementLineListResult,
  IOresundPayData,
  IOresundPayDataResponse,
  IAssetManagementListResult,
  IAsset,
  IOutboundEfcContextDataListResult,
  IExceptionListChunksResult,
  IInboundEfcContextDataListResult,
  IAbnormalBehaviorReportDataListResult,
  IAcceptedObject,
  AcceptedObjectMessageType,
  IAcceptedObjectResponse,
  AckNackRequestTypeEnum,
  InboundStatusAduListResult,
  ITransaction,
  ITransactionListResult,
  IPreset,
} from 'types/apiTypes';
import { osbApiAxios } from './axiosUtils';
import { AxiosError } from 'axios';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { convertCopenhagenDateToUtcISOString } from './dateTime';

// #region Preset Values

interface IPresetTransitions extends IPreset {
  allowedTransitions: string[];
}

interface IPresetValuesResponse {
  tollChargers: IPreset[];
  actors: IPreset[];
  actionCodes: IPreset[];
  ackTypes: IPreset[];
  kapschEventTypes: IPreset[];
  tollSchemes: IPreset[];
  paymentClaimTypeOfContent: IPreset[];
  assetStatuses: IPresetTransitions[];
  exceptionListTypes: IPreset[];
}

export const getPresetValues = async (): Promise<IPresetValuesResponse> => {
  const response = await osbApiAxios.get<IPresetValuesResponse>('/v1/Presets');
  return response.data;
};

// #endregion Preset Values

// #region Tolling Admin Access

export const getTollingAdminAccess = async (): Promise<ITollingAdminAccessObject> => {
  const response = await osbApiAxios.get<ITollingAdminAccessObject>('/v1/TollingAdminAccess');
  return response.data;
};

export const postTollingAdminAccess = async (newAccessObject: ITollingAdminAccessObject) => {
  return osbApiAxios.post('/v1/TollingAdminAccess', newAccessObject);
};

// #endregion Tolling Admin Access

// #region AckNackDownload

export const getAckNackDownload = async (
  countryCode: string,
  providerIdentifier: number,
  apduIdentifier: string,
  requestType: AckNackRequestTypeEnum
): Promise<string> => {
  const response = await osbApiAxios.get<string>('/v1/Acknowledgement/download', {
    params: {
      countryCode,
      providerIdentifier,
      apduIdentifier,
      requestType,
    },
    transformResponse: (r) => r,
  });
  return response.data;
};

// #endregion AckNackDownload

// #region Billing Details
interface IGetBillingDetailsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IBillingDetailsListFilter {
  createdAt: DateRange | undefined;
  tc: string;
  obeId: string;
  lpn: string;
  pan: string;
  beginOfPeriod: DateRange | undefined;
  endOfPeriod: DateRange | undefined;
  dateOfService: DateRange | undefined;
  billingDetailsNum: string;
  actionCode: string;
  acceptedByBo: string;
  paymentCaptureId: string;
  ackStatus: string;
  apduDate: DateRange | undefined;
  apduIdentifier: string;
  apduOriginator: string;
  senderId: string;
  aduIdentifier: string;
}

interface IBillingDetailsQueryParams {
  createdAtStart: string;
  createdAtEnd: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  apduIdentifier?: string;
  apduOriginator?: string;
  senderId?: string;
  aduIdentifier?: string;
  obeIdEquipmentObuId?: string;
  licensePlateNumber?: string;
  userIdPan?: string;
  periodBeginOfPeriodStart?: string;
  periodBeginOfPeriodEnd?: string;
  periodEndOfPeriodStart?: string;
  periodEndOfPeriodEnd?: string;
  dateOfServiceStart?: string;
  dateOfServiceEnd?: string;
  billingDetailsNum?: string;
  aduActionCode?: string;
  acceptedByBO?: string;
  paymentCaptureId?: string;
  ackType?: string;
}

export const getBillingDetailsList = async (
  page: number,
  pageSize: number,
  filters: Partial<IBillingDetailsListFilter>,
  sortOrder?: string
): Promise<IBillingDetailsListResult> => {
  const createdAtEnd = filters.createdAt?.to ? filters.createdAt?.to : filters.createdAt?.from;
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const periodBeginOfPeriodEnd = filters.beginOfPeriod?.to
    ? filters.beginOfPeriod?.to
    : filters.beginOfPeriod?.from;
  const periodEndOfPeriodEnd = filters.endOfPeriod?.to
    ? filters.endOfPeriod?.to
    : filters.endOfPeriod?.from;
  const dateOfServiceEnd = filters.dateOfService?.to
    ? filters.dateOfService?.to
    : filters.dateOfService?.from;

  const appliedFilters: IBillingDetailsQueryParams = {
    createdAtEnd: createdAtEnd ? convertCopenhagenDateToUtcISOString(createdAtEnd) : '',
    createdAtStart: filters.createdAt?.from
      ? convertCopenhagenDateToUtcISOString(filters.createdAt?.from)
      : '',

    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    apduIdentifier: filters.apduIdentifier,
    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    aduIdentifier: filters.aduIdentifier,
    obeIdEquipmentObuId: filters.obeId,
    licensePlateNumber: filters.lpn,
    userIdPan: filters.pan,
    periodBeginOfPeriodStart: filters.beginOfPeriod?.from
      ? convertCopenhagenDateToUtcISOString(filters.beginOfPeriod?.from)
      : undefined,
    periodBeginOfPeriodEnd: periodBeginOfPeriodEnd
      ? convertCopenhagenDateToUtcISOString(periodBeginOfPeriodEnd)
      : undefined,
    periodEndOfPeriodStart: filters.endOfPeriod?.from
      ? convertCopenhagenDateToUtcISOString(filters.endOfPeriod?.from)
      : undefined,
    periodEndOfPeriodEnd: periodEndOfPeriodEnd
      ? convertCopenhagenDateToUtcISOString(periodEndOfPeriodEnd)
      : undefined,

    dateOfServiceStart: filters.dateOfService?.from
      ? convertCopenhagenDateToUtcISOString(filters.dateOfService?.from)
      : undefined,
    dateOfServiceEnd: dateOfServiceEnd
      ? convertCopenhagenDateToUtcISOString(dateOfServiceEnd)
      : undefined,

    billingDetailsNum: filters.billingDetailsNum,
    aduActionCode: filters.actionCode,
    acceptedByBO: filters.acceptedByBo ? filters.acceptedByBo.toString() : undefined,
    paymentCaptureId: filters.paymentCaptureId,
    ackType: filters.ackStatus,
  };

  const params: IGetBillingDetailsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IBillingDetailsListResult>('/v1/BillingDetails', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getBillingDetails = async (billingDetailsId: string): Promise<IBillingDetails> => {
  const response = await osbApiAxios.get<IBillingDetails>(`/v1/BillingDetails/${billingDetailsId}`);
  return response.data;
};

export const getBillingDetailsFile = async (billingDetailsId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/BillingDetails/${billingDetailsId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

export const postBillingDetailResend = async (billingDetailsId: string) => {
  return osbApiAxios.post<string>(`/v1/BillingDetails/${billingDetailsId}/resend`);
};

// #endregion Billing Details

// #region Toll Declaration
interface IGetTollDetailsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface ITollDeclarationsListFilter {
  apduDate: DateRange | undefined;
  apduIdentifier: string;
  apduOriginator: string;
  senderId: string;
  recipientId: string;
  aduIdentifier: string;
  obeIdEquipmentObuId: string;
  licensePlateNumber: string;
  userIdPan: string;
  chargeReportCounter: string;
}
interface ITollDeclarationsQueryParams {
  createdAtStart?: string;
  createdAtEnd?: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  apduIdentifier?: string;
  apduOriginator?: string;
  senderId?: string;
  recipientId?: string;
  aduIdentifier?: string;
  obeIdEquipmentObuId?: string;
  licensePlateNumber?: string;
  userIdPan?: string;
  chargeReportCounter?: string;
}
export const getTollDeclarationsList = async (
  page: number,
  pageSize: number,
  filters: Partial<ITollDeclarationsListFilter>,
  sortOrder?: string
): Promise<ITollDeclarationsListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;

  const appliedFilters: ITollDeclarationsQueryParams = {
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    apduIdentifier: filters.apduIdentifier,
    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    recipientId: filters.recipientId,
    aduIdentifier: filters.aduIdentifier,
    obeIdEquipmentObuId: filters.obeIdEquipmentObuId,
    licensePlateNumber: filters.licensePlateNumber,
    userIdPan: filters.userIdPan,
    chargeReportCounter: filters.chargeReportCounter,
  };

  const params: IGetTollDetailsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<ITollDeclarationsListResult>('/v1/TollDeclaration', {
    params,
  });

  // @ts-expect-error ignore getContentType type

  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};
export const getTollDeclaration = async (tollDeclarationId: string): Promise<ITollDeclaration> => {
  const response = await osbApiAxios.get<ITollDeclaration>(
    `/v1/TollDeclaration/${tollDeclarationId}`
  );
  return response.data;
};
// #endregion Toll Declaration

// #region Payment Claim
interface IGetPaymentClaimsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IPaymentClaimsListFilter {
  id: string;
  createdAt: DateRange | undefined;
  apduOriginator: string | undefined;
  senderId: string | undefined;
  startDateTime: DateRange | undefined;
  endDateTime: DateRange | undefined;
  paymentDetailAmountTotalMin: string | undefined;
  paymentDetailAmountTotalMax: string | undefined;
  paymentReference: string | undefined;
  typeOfContent: number | undefined;
}
interface IPaymentClaimQueryParams {
  createdAtStart?: string;
  createdAtEnd?: string;
  apduOriginator?: string;
  senderId?: string;
  startDateTimeStart?: string;
  startDateTimeEnd?: string;
  endDateTimeStart?: string;
  endDateTimeEnd?: string;
  paymentDetailAmountTotalMin?: string;
  paymentDetailAmountTotalMax?: string;
  paymentReference?: string;
  typeOfContent?: number;
}
export const getPaymentClaimsList = async (
  page: number,
  pageSize: number,
  filters: Partial<IPaymentClaimsListFilter>,
  sortOrder?: string
): Promise<IPaymentClaimsListResult> => {
  const createdAtEnd = filters.createdAt?.to ? filters.createdAt?.to : filters.createdAt?.from;
  const startDateTimeEnd = filters.startDateTime?.to
    ? filters.startDateTime?.to
    : filters.startDateTime?.from;
  const endDateTimeEnd = filters.endDateTime?.to
    ? filters.endDateTime?.to
    : filters.endDateTime?.from;

  const appliedFilters: IPaymentClaimQueryParams = {
    createdAtStart: filters.createdAt?.from
      ? convertCopenhagenDateToUtcISOString(filters.createdAt?.from)
      : undefined,
    createdAtEnd: createdAtEnd ? convertCopenhagenDateToUtcISOString(createdAtEnd) : undefined,

    startDateTimeStart: filters.startDateTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.startDateTime?.from)
      : undefined,
    startDateTimeEnd: startDateTimeEnd
      ? convertCopenhagenDateToUtcISOString(startDateTimeEnd)
      : undefined,

    endDateTimeStart: filters.endDateTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.endDateTime?.from)
      : undefined,
    endDateTimeEnd: endDateTimeEnd
      ? convertCopenhagenDateToUtcISOString(endDateTimeEnd)
      : undefined,

    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,

    paymentDetailAmountTotalMin: filters.paymentDetailAmountTotalMin,
    paymentDetailAmountTotalMax: filters.paymentDetailAmountTotalMax,
    paymentReference: filters.paymentReference,
    typeOfContent: filters.typeOfContent,
  };

  const params: IGetPaymentClaimsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IPaymentClaimsListResult>(`/v1/PaymentClaim`, { params });

  // @ts-expect-error ignore getContentType type

  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getPaymentClaim = async (paymentClaimId: string): Promise<IPaymentClaim> => {
  const response = await osbApiAxios.get<IPaymentClaim>(`/v1/PaymentClaim/${paymentClaimId}`);
  return response.data;
};

export const postPaymentClaimAnnouncement = async (
  paymentClaimId: string,
  dueDate: Date,
  valueDate: Date
): Promise<string> => {
  const response = await osbApiAxios.post<string>(
    `/v1/PaymentClaim/${paymentClaimId}/sendAnnouncement`,
    {
      dueDate: dueDate.toISOString(),
      valueDate: valueDate.toISOString(),
    }
  );
  return response.data;
};

export const getPaymentClaimFile = async (paymentClaimId: string): Promise<string> => {
  // yes it's v1/PaymentClaims/ for just this url
  const response = await osbApiAxios.get<string>(`/v1/PaymentClaims/${paymentClaimId}/download`, {
    transformResponse: (r) => r,
  });
  return response.data;
};

// #endregion Payment Claim

// #region Audit Log
interface IGetAuditLogListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IAuditLogListFilter {
  actor: string;
  obeEquipmentId: string;
  eventSeverity: string;
  date: DateRange | undefined;
  freeText: string;
}
interface IAuditLogListQueryParams {
  actor?: string;
  obeEquipmentId?: string;
  eventSeverity?: string;
  startTime?: string;
  endTime?: string;
  freeText?: string;
}
export const getAuditLogList = async (
  page: number,
  pageSize: number,
  filters: Partial<IAuditLogListFilter>,
  sortOrder?: string
): Promise<IAuditLogListResult> => {
  const endTime = filters.date?.to ? filters.date?.to : filters.date?.from;

  const appliedFilters: IAuditLogListQueryParams = {
    startTime: filters.date?.from
      ? convertCopenhagenDateToUtcISOString(filters.date?.from)
      : undefined,
    endTime: endTime ? convertCopenhagenDateToUtcISOString(endTime) : undefined,
    actor: filters.actor,
    obeEquipmentId: filters.obeEquipmentId,
    eventSeverity: filters.eventSeverity,
    freeText: filters.freeText,
  };
  const params: IGetAuditLogListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IAuditLogListResult>('/v1/AuditLogEvents', {
    params,
  });
  return response.data;
};
// #endregion Audit Log

// #region OBE event Log
interface IGetObeEventsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IObeEventsListFilter {
  date: DateRange | undefined;
  obuEquipmentId: string;
  type: string;
}

export interface IObeKapschEventsListFilter {
  date: DateRange | undefined;
  obuEquipmentId: string;
}

export interface IObeFineHelperFilter {
  date: DateRange | undefined;
  time: string;
  obuEquipmentId: string;
  pan: string;
}

interface IObeEventsQueryParams {
  dateFrom?: string;
  dateTo?: string;
  obuEquipmentId?: string;
  type?: string;
}

export const getObeEventLog = async (
  page: number,
  pageSize: number,
  filters: Partial<IObeEventsListFilter>,
  sortOrder?: string
): Promise<IObeEventLogResult> => {
  const dateEnd = filters.date?.to ? filters.date?.to : filters.date?.from;

  const appliedFilters: IObeEventsQueryParams = {
    dateFrom: filters.date?.from ? format(filters.date?.from, 'yyyy-MM-dd') : '',
    dateTo: dateEnd ? format(dateEnd, 'yyyy-MM-dd') : '',
    obuEquipmentId: filters.obuEquipmentId,
    type: filters.type,
  };
  const params: IGetObeEventsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IObeEventLogResult>('/v1/KapschEvents', {
    params,
  });
  // @ts-expect-error ignore getContentType type

  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

interface IObeEventsQueryParams {
  dateFrom?: string;
  dateTo?: string;
  obuEquipmentId?: string;
  type?: string;
}

interface IObeKapschEventsQueryParams {
  obeEquipmentId: string;
  manufacturerId: number;
  timeFrom: string;
  timeTo: string;
  timeZone: string;
}

export const getObeKapschEventLog = async (
  page: number,
  pageSize: number,
  filters: Partial<IObeKapschEventsListFilter>,
  sortOrder?: string
): Promise<string> => {
  const dateEnd = filters.date?.to ? filters.date?.to : filters.date?.from;
  const today = format(new Date(), 'yyyy-MM-dd');
  const formattedDateEnd = dateEnd
    ? format(dateEnd, 'yyyy-MM-dd') === today
      ? new Date().toISOString()
      : format(dateEnd, 'yyyy-MM-dd') + 'T23:59:59'
    : '';

  const appliedFilters: IObeKapschEventsQueryParams = {
    obeEquipmentId: filters.obuEquipmentId ?? '',
    manufacturerId: 3, // Default to Kapsch manufacturer ID
    timeFrom: filters.date?.from ? format(filters.date?.from, 'yyyy-MM-dd') : '',
    timeTo: formattedDateEnd,
    timeZone: 'Europe/Copenhagen',
  };
  const params: IGetObeEventsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<string>('/KapschObuEventLog', {
    // baseURL: 'http://localhost:3333',
    params,
  });

  return response.data;
};

// #endregion OBE event Log

// #region Asset Management

interface IGetAssetManagementListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IAssetManagementListFilter {
  pan1: string;
  pan2: string;
  licensePlateNumber: string;
  obeEquipmentId: string;
  connectivityStatus: string;
  obeStatus: string;
  manufacturerId: string;
  assetStatus: string;

  partNo: string;
  productionDate: DateRange | undefined;
  msn_Luhn: string;
  articleNo: string;
  csn: string;
  iccId: string;
  imsi: string;
  imei: string;
  boxNo: string;
  palletNo: string;
  deliveryNoteDate: DateRange | undefined;

  // lastUsedInTransaction: DateRange | undefined;
  // lastDistributedToCustomer: DateRange | undefined;
}

interface IAssetManagementListQueryParams {
  pan1?: string;
  pan2?: string;
  licensePlateNumber?: string;
  obeEquipmentId?: string;
  connectivityStatus?: string;
  obeStatus?: string;
  manufacturerId?: string;
  assetStatus?: string;

  partNo?: string;
  productionDateFrom?: string;
  productionDateTo?: string;
  msn_Luhn?: string;
  articleNo?: string;
  csn?: string;
  iccId?: string;
  imsi?: string;
  imei?: string;
  boxNo?: string;
  palletNo?: string;
  deliveryNoteDateFrom?: string;
  deliveryNoteDateTo?: string;

  // lastUsedInTransactionFrom?: string;
  // lastUsedInTransactionTo?: string;
  // lastDistributedToCustomerFrom?: string;
  // lastDistributedToCustomerTo?: string;
}

export const getAssetManagementList = async (
  page: number,
  pageSize: number,
  filters: Partial<IAssetManagementListFilter>,
  sortOrder?: string
): Promise<IAssetManagementListResult> => {
  const productionDateTo = filters.productionDate?.to
    ? filters.productionDate?.to
    : filters.productionDate?.from;
  const deliveryNoteDateTo = filters.deliveryNoteDate?.to
    ? filters.deliveryNoteDate?.to
    : filters.deliveryNoteDate?.from;

  const appliedFilters: IAssetManagementListQueryParams = {
    pan1: filters.pan1,
    pan2: filters.pan2,
    licensePlateNumber: filters.licensePlateNumber,
    obeEquipmentId: filters.obeEquipmentId,
    connectivityStatus: filters.connectivityStatus,
    obeStatus: filters.obeStatus,
    manufacturerId: filters.manufacturerId,
    assetStatus: filters.assetStatus,
    partNo: filters.partNo,
    msn_Luhn: filters.msn_Luhn,
    articleNo: filters.articleNo,
    csn: filters.csn,
    iccId: filters.iccId,
    imsi: filters.imsi,
    imei: filters.imei,
    boxNo: filters.boxNo,
    palletNo: filters.palletNo,
    productionDateFrom: filters.productionDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.productionDate?.from)
      : undefined,
    productionDateTo: productionDateTo
      ? convertCopenhagenDateToUtcISOString(productionDateTo)
      : undefined,
    deliveryNoteDateFrom: filters.deliveryNoteDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.deliveryNoteDate?.from)
      : undefined,
    deliveryNoteDateTo: deliveryNoteDateTo
      ? convertCopenhagenDateToUtcISOString(deliveryNoteDateTo)
      : undefined,
  };
  const params: IGetAssetManagementListQueryParams = {
    page: page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IAssetManagementListResult>('/v1/Asset', {
    params,
  });
  // @ts-expect-error ignore getContentType type

  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getAssetDetails = async (pan2: string): Promise<IAsset> => {
  const response = await osbApiAxios.get<IAsset>(`/v1/asset/${pan2}`);
  if (response.status === 204) {
    throw new AxiosError(
      `Asset with id:"${pan2}" not found`,
      response.status.toString(),
      response.config
    );
  }
  return response.data;
};

export const putAssetStatus = async (
  pan2: string,
  newStatus: string,
  comment: string
): Promise<void> => {
  await osbApiAxios.put(`/v1/asset/${pan2}`, {
    assetStatus: newStatus,
    comment,
  });
};

export const putBulkAssetStatus = async (
  inputMethod: string,
  values: string[],
  newStatus: string,
  comment: string
): Promise<void> => {
  await osbApiAxios.put('/v1/asset/', {
    assetStatus: newStatus,
    comment,
    assetId: values,
    updateType: parseInt(inputMethod, 10),
  });
};

// #endregion Asset Management

// #region OBE registry
interface IGetObeListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IObeListFilter {
  pan: string;
  licensePlateNumber: string;
  obeEquipmentId: string;
  obeStatus: string;
  connectivityStatus: string;
}

interface IObeListQueryParams {
  pan?: string;
  licensePlateNumber?: string;
  obeEquipmentId?: string;
  obeStatus?: string;
  connectivityStatus?: string;
}

export const getObeList = async (
  page: number,
  pageSize: number,
  filters: Partial<IObeListFilter>,
  sortOrder?: string
): Promise<IObeListResult> => {
  const appliedFilters: IObeListQueryParams = {
    pan: filters.pan,
    licensePlateNumber: filters.licensePlateNumber,
    obeEquipmentId: filters.obeEquipmentId,
    obeStatus: filters.obeStatus,
    connectivityStatus: filters.connectivityStatus,
  };
  const params: IGetObeListQueryParams = {
    page: page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IObeListResult>('/v1/Obe', {
    params,
  });
  // @ts-expect-error ignore getContentType type

  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getObeDetails = async (obeEquipmentId: string): Promise<IObeDetails> => {
  const response = await osbApiAxios.get<IObeDetails>(`/v1/Obe/${obeEquipmentId}`);
  if (response.status === 204) {
    throw new AxiosError(
      `OBE with id:"${obeEquipmentId}" not found`,
      response.status.toString(),
      response.config
    );
  }
  return response.data;
};
// #endregion OBE registry

// #region Exception Lists
interface IGetExceptionListsQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
interface IGetExceptionListChunksQueryParams {
  AduIdentifier: string;
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
interface IExceptionListsQueryParams {
  obeIdEquipmentObuId?: string;
  recipientId?: string;
  apduIdentifier?: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  aduIdentifier?: string;
  exceptionListType?: string;
  exceptionListVersion?: string;
  actionCode?: string;
  ackStatus?: string;
}
export interface IExceptionListsFilter {
  obeIdEquipmentObuId: string;
  recipientId: string;
  apduIdentifier: string;
  apduDate: DateRange | undefined;
  aduIdentifier: string;
  exceptionListType: string;
  exceptionListVersion: string;
  actionCode: string;
  ackStatus: string;
}

export const getExceptionLists = async (
  page: number,
  pageSize: number,
  filters: Partial<IExceptionListsFilter>,
  sortOrder?: string
): Promise<IExceptionListsResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;

  const appliedFilters: IExceptionListsQueryParams = {
    obeIdEquipmentObuId: filters.obeIdEquipmentObuId,
    recipientId: filters.recipientId,
    apduIdentifier: filters.apduIdentifier,
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    aduIdentifier: filters.aduIdentifier,
    exceptionListType: filters.exceptionListType,
    exceptionListVersion: filters.exceptionListVersion,
    actionCode: filters.actionCode,
    ackStatus: filters.ackStatus,
  };
  const params: IGetExceptionListsQueryParams = {
    page: page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  // console.log(`TODO: Update getExceptionLists in api.ts to fetch real data.`);
  const response = await osbApiAxios.get<IExceptionListsResult>('/v1/ExceptionList', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getExceptionListChunks = async (
  exceptionListId: string,
  aduIdentifier: string,
  page: number,
  pageSize: number,
  sortOrder?: string
): Promise<IExceptionListChunksResult> => {
  const params: IGetExceptionListChunksQueryParams = {
    page: page,
    pageSize,
    AduIdentifier: aduIdentifier,
    sortOrder,
  };

  const response = await osbApiAxios.get<IExceptionListChunksResult>(
    `/v1/ExceptionList/${exceptionListId}/chunks`,
    { params }
  );
  return response.data;
};

export const getExceptionListDetails = async (exceptionListId: string): Promise<IExceptionList> => {
  const response = await osbApiAxios.get<IExceptionList>(`/v1/ExceptionList/${exceptionListId}`);
  return response.data;
};

export const getExceptionListFile = async (exceptionListId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(`/v1/ExceptionList/${exceptionListId}/download`, {
    transformResponse: (r) => r,
  });
  return response.data;
};

export const postSendFullExceptionList = async (actorCode: string, type: string): Promise<void> => {
  await osbApiAxios.post(
    '/v1/ExceptionList/send/full',
    {
      // actorCode,
      // type,
    },
    {
      params: {
        actorCode,
        type,
      },
    }
  );
};

export const getExceptionListChunkFile = async (
  exceptionListChunkId: string,
  blobRef: string
): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/ExceptionList/full/${exceptionListChunkId}/download`,
    {
      params: { blobRef },
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Exception Lists

// #region Toll Chargers
export interface ITollChargersListFilter {
  name: string;
  countryCode: string;
  providerIdentifier: string;
  actorId: string;
  tollScheme: string;
}

interface ITollChargersListQueryParams {
  name?: string;
  countryCode?: string;
  providerIdentifier?: string;
  actorId?: string;
  tollScheme?: string;
}

export const getTollChargersList = async (
  page: number,
  pageSize: number,
  filters: Partial<ITollChargersListFilter>,
  sortOrder?: string
): Promise<ITollChargersResponse> => {
  const appliedFilters: ITollChargersListQueryParams = {
    name: filters.name,
    countryCode: filters.countryCode,
    providerIdentifier: filters.providerIdentifier,
    actorId: filters.actorId,
    tollScheme: filters.tollScheme,
  };

  const params: IGetTollDetailsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<ITollChargersResponse>('/v1/TollCharger', { params });
  return response.data;
};

export const getTollCharger = async (tollChargerId: string): Promise<ITollCharger> => {
  const response = await osbApiAxios.get<ITollCharger>(`/v1/TollCharger/${tollChargerId}`);
  return response.data;
};

export const postTollCharger = async (newTollCharger: ITollCharger) => {
  return osbApiAxios.post<ITollCharger>('/v1/TollCharger', newTollCharger);
};

export const putTollCharger = async (newTollCharger: ITollCharger) => {
  return osbApiAxios.put<ITollCharger>(`/v1/TollCharger/${newTollCharger.id}`, newTollCharger);
};

// #endregion Toll Chargers

// #region EFC Context Marks
interface IGetEfcContextMarksListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IEfcContextMarksListFilter {
  efcContextMarkVersion: number | undefined;
  manufactureId: number | undefined;
  uniquePartOfPAN: string | undefined;
  typeOfPan: string | undefined;
  versionAndValidityValidFrom: DateRange | undefined;
  versionAndValidityVersion: string | undefined;
  versionAndValidityEntryValidFrom: DateRange | undefined;
  versionAndValidityEntryVersion: string | undefined;
  typeOfContract: string | undefined;
  contextVersion: number | undefined;
  obuFriendlyName: string | undefined;
  obuEID: number | undefined;
}

interface IEfcContextMarksListQueryParams {
  efcContextMarkVersion?: string;
  manufactureId?: string;
  uniquePartOfPAN?: string;
  typeOfPan?: string;
  versionAndValidityValidFrom?: string;
  versionAndValidityVersion?: string;
  versionAndValidityEntryValidFrom?: string;
  versionAndValidityEntryVersion?: string;
  typeOfContract?: string;
  contextVersion?: string;
  obuFriendlyName?: string;
  obuEID?: string;
}

export const getEfcContextMarksList = async (
  page: number,
  pageSize: number,
  filters: Partial<IEfcContextMarksListFilter>,
  sortOrder?: string
): Promise<IEfcContextMarksListResult> => {
  const appliedFilters: IEfcContextMarksListQueryParams = {
    efcContextMarkVersion: filters.efcContextMarkVersion
      ? filters.efcContextMarkVersion.toString()
      : undefined,
    manufactureId: filters.manufactureId ? filters.manufactureId.toString() : undefined,
    uniquePartOfPAN: filters.uniquePartOfPAN,
    typeOfPan: filters.typeOfPan,
    versionAndValidityVersion: filters.versionAndValidityVersion,
    versionAndValidityEntryVersion: filters.versionAndValidityEntryVersion,

    typeOfContract: filters.typeOfContract,
    contextVersion: filters.contextVersion ? filters.contextVersion.toString() : undefined,
    obuFriendlyName: filters.obuFriendlyName,
    obuEID: filters.obuEID ? filters.obuEID.toString() : undefined,
  };

  const params: IGetEfcContextMarksListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IEfcContextMarksListResult>('/v1/EFCContextMark', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getEfcContextMark = async (efcContextMarkId: string): Promise<IEfcContextMark> => {
  const response = await osbApiAxios.get<IEfcContextMark>(`/v1/EFCContextMark/${efcContextMarkId}`);
  return response.data;
};

export const postEfcContextMark = async (newEfcContextMark: IEfcContextMark) => {
  return osbApiAxios.post<IEfcContextMark>(`/v1/EFCContextMark`, newEfcContextMark);
};

export const putEfcContextMark = async (newEfcContextMark: IEfcContextMark) => {
  return osbApiAxios.put<IEfcContextMark>(
    `/v1/EFCContextMark/${newEfcContextMark.id}`,
    newEfcContextMark
  );
};

// #endregion EFC Context Marks

// #region Outbound EFC Context Data

interface IGetOutboundEfcContextDataListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IOutboundEfcContextDataListFilter {
  recipientId: string | undefined;
  apduIdentifier: string | undefined;
  apduDate: DateRange | undefined;
  sent: DateRange | undefined;
}

interface IOutboundEfcContextDataListQueryParams {
  recipientId?: string;
  apduIdentifier?: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  sentStart?: string;
  sentEnd?: string;
}

export const getOutboundEfcContextDataList = async (
  page: number,
  pageSize: number,
  filters: Partial<IOutboundEfcContextDataListFilter>,
  sortOrder?: string
): Promise<IOutboundEfcContextDataListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const sentEnd = filters.sent?.to ? filters.sent?.to : filters.sent?.from;

  const appliedFilters: IOutboundEfcContextDataListQueryParams = {
    recipientId: filters.recipientId,
    apduIdentifier: filters.apduIdentifier,
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    sentStart: filters.sent?.from
      ? convertCopenhagenDateToUtcISOString(filters.sent?.from)
      : undefined,
    sentEnd: sentEnd ? convertCopenhagenDateToUtcISOString(sentEnd) : undefined,
  };

  const params: IGetOutboundEfcContextDataListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IOutboundEfcContextDataListResult>(
    '/v1/OutboundEfcContextData',
    {
      params,
    }
  );

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getEfcContextDataOutboundFile = async (efcContextDataId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/OutboundEfcContextData/${efcContextDataId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Outbound EFC Context Data

// #region Inbound EFC Context Data

interface IGetInboundEfcContextDataListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IInboundEfcContextDataListFilter {
  senderId: string | undefined;
  apduIdentifier: string | undefined;
  apduOriginator: string | undefined;
  apduDate: DateRange | undefined;
  received: DateRange | undefined;
}

interface IInboundEfcContextDataListQueryParams {
  senderId?: string;
  apduIdentifier?: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  receivedStart?: string;
  receivedEnd?: string;
}

export const getInboundEfcContextDataList = async (
  page: number,
  pageSize: number,
  filters: Partial<IInboundEfcContextDataListFilter>,
  sortOrder?: string
): Promise<IInboundEfcContextDataListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const receivedEnd = filters.received?.to ? filters.received?.to : filters.received?.from;

  const appliedFilters: IInboundEfcContextDataListQueryParams = {
    senderId: filters.senderId,
    apduIdentifier: filters.apduIdentifier,
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    receivedStart: filters.received?.from
      ? convertCopenhagenDateToUtcISOString(filters.received?.from)
      : undefined,
    receivedEnd: receivedEnd ? convertCopenhagenDateToUtcISOString(receivedEnd) : undefined,
  };

  const params: IGetInboundEfcContextDataListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IInboundEfcContextDataListResult>(
    '/v1/InboundEfcContextData',
    {
      params,
    }
  );

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getEfcContextDataInboundFile = async (efcContextDataId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/InboundEfcContextData/${efcContextDataId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Inbound EFC Context Data

// #region Inbound StatusAdu

interface IGetInboundStatusAduListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IInboundStatusAduListFilter {
  senderId: string | undefined;
  apduIdentifier: string | undefined;
  apduDate: DateRange | undefined;
  received: DateRange | undefined;
}

interface IInboundStatusAduListQueryParams {
  senderId?: string;
  apduIdentifier?: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  receivedStart?: string;
  receivedEnd?: string;
}

export const getInboundStatusAduList = async (
  page: number,
  pageSize: number,
  filters: Partial<IInboundStatusAduListFilter>,
  sortOrder?: string
): Promise<InboundStatusAduListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const receivedEnd = filters.received?.to ? filters.received?.to : filters.received?.from;

  const appliedFilters: IInboundStatusAduListQueryParams = {
    senderId: filters.senderId,
    apduIdentifier: filters.apduIdentifier,
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    receivedStart: filters.received?.from
      ? convertCopenhagenDateToUtcISOString(filters.received?.from)
      : undefined,
    receivedEnd: receivedEnd ? convertCopenhagenDateToUtcISOString(receivedEnd) : undefined,
  };

  const params: IGetInboundStatusAduListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<InboundStatusAduListResult>('/v1/inboundStatusAdu', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getInboundStatusAduFile = async (inboundStatusAduId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/inboundStatusAdu/${inboundStatusAduId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Inbound StatusAdu

// #region Contract Issuer List

export interface IContractIssuerListFilter {
  apduOriginator: string;
  senderId: string;
  recipientId: string;
  apduIdentifier: string;
  apduDate: DateRange | undefined;
  sent: DateRange | undefined;
}

interface IContractIssuerListQueryParams {
  apduOriginator?: string;
  senderId?: string;
  recipientId?: string;
  apduIdentifier?: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  sentStart?: string;
  sentEnd?: string;
}

export const getContractIssuerList = async (
  page: number,
  pageSize: number,
  filters: Partial<IContractIssuerListFilter>,
  sortOrder?: string
): Promise<IContractIssuerListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const sentEnd = filters.sent?.to ? filters.sent?.to : filters.sent?.from;

  const appliedFilters: IContractIssuerListQueryParams = {
    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    recipientId: filters.recipientId,
    apduIdentifier: filters.apduIdentifier,
    sentStart: filters.sent?.from
      ? convertCopenhagenDateToUtcISOString(filters.sent?.from)
      : undefined,
    sentEnd: sentEnd ? convertCopenhagenDateToUtcISOString(sentEnd) : undefined,
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
  };

  const params: IGetTollDetailsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IContractIssuerListResult>('/v1/ContractIssuerList', {
    params,
  });
  return response.data;
};

export const getContractIssuerDetails = async (
  contractIssuerId: string
): Promise<IContractIssuerList> => {
  const response = await osbApiAxios.get<IContractIssuerList>(
    `/v1/ContractIssuerList/${contractIssuerId}`
  );
  return response.data;
};

export const postContractIssuerListSend = async (actorCodes: string[]) => {
  return osbApiAxios.post<string>('v1/ContractIssuerList/send', {
    actorCodes,
  });
};
// #endregion Contract Issuer List

// #region Transactions

interface IGetTransactionsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface ITransactionsListFilter {
  transactionDateTime: DateRange | undefined;
  transactionId: string;
  agreementId: string;
  amountWithoutVatMin: string;
  amountWithoutVatMax: string;
  senderActorCode: string;
  pan1: string;
  pan2: string;
  panUsed: string;
  lpn: string;
  lpnCountryCode: string;
  transactionType: string;
  paymentClaimReference: string;
  billingDetailAduIdentifier: string;
  acceptedByBO: string;
}

interface ITransactionsListQueryParams {
  TransactionDateTimeFrom?: string;
  TransactionDateTimeTo?: string;
  TransactionId?: string;
  AgreementId?: string;
  AmountWithoutVatMin?: number;
  AmountWithoutVatMax?: number;
  SenderActorCode?: string;
  Pan1?: string;
  Pan2?: string;
  PanUsed?: string;
  Lpn?: string;
  LpnCountryCode?: string;
  TransactionType?: string;
  PaymentClaimReference?: string;
  BillingDetailAduIdentifier?: string;
  AcceptedByBO?: string;
}

export const getTransactionsList = async (
  page: number,
  pageSize: number,
  filters: Partial<ITransactionsListFilter>,
  sortOrder?: string
): Promise<ITransactionListResult> => {
  const transactionDateTimeTo = filters.transactionDateTime?.to
    ? filters.transactionDateTime?.to
    : filters.transactionDateTime?.from;

  const appliedFilters: ITransactionsListQueryParams = {
    TransactionDateTimeFrom: filters.transactionDateTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.transactionDateTime?.from)
      : undefined,
    TransactionDateTimeTo: transactionDateTimeTo
      ? convertCopenhagenDateToUtcISOString(transactionDateTimeTo)
      : undefined,
    TransactionId: filters.transactionId,
    AgreementId: filters.agreementId,
    AmountWithoutVatMin: filters.amountWithoutVatMin
      ? parseFloat(filters.amountWithoutVatMin)
      : undefined,
    AmountWithoutVatMax: filters.amountWithoutVatMax
      ? parseFloat(filters.amountWithoutVatMax)
      : undefined,
    SenderActorCode: filters.senderActorCode,
    Pan1: filters.pan1,
    Pan2: filters.pan2,
    PanUsed: filters.panUsed,
    Lpn: filters.lpn,
    LpnCountryCode: filters.lpnCountryCode,
    TransactionType: filters.transactionType,
    PaymentClaimReference: filters.paymentClaimReference,
    BillingDetailAduIdentifier: filters.billingDetailAduIdentifier,
    AcceptedByBO: filters.acceptedByBO,
  };

  const params: IGetTransactionsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<ITransactionListResult>('/v1/TransactionData', {
    params,
  });
  return response.data;
};

export const getTransactionsDetails = async (transactionId: string): Promise<ITransaction> => {
  const response = await osbApiAxios.get<ITransaction>(`/v1/TransactionData/${transactionId}`);
  return response.data;
};

// #endregion Transactions

// #region Agreement lines

export interface IAgreementLineListFilter {
  paymentIdentifier: string;
  agreementLineId: string;
  agreementId: string;
  replacementPaymentIdentifier: string;
  replacementAgreementLineId: string;
  type: string;
  licensePlateNumber: string;
  licensePlateCountryCode: string;
  endDate: DateRange | undefined;
  invoiceReference: string;
  reasonCode: string;
  status: string;
  enabledInTollDomains: IPreset[]; // actor codes
  lastModified: DateRange | undefined;
}

interface IAgreementLineListQueryParams {
  paymentIdentifier?: string;
  agreementLineId?: string;
  agreementId?: string;
  replacementPaymentIdentifier?: string;
  replacementAgreementLineId?: string;
  type?: string;
  licensePlateNumber?: string;
  licensePlateCountryCode?: string;
  endDateStart?: string;
  endDateEnd?: string;
  invoiceReference?: string;
  reasonCode?: string;
  status?: string;
  enabledInTollDomains?: string; // comma separated actor codes
  lastModifiedStart?: string;
  lastModifiedEnd?: string;
}

export const getAgreementLineList = async (
  page: number,
  pageSize: number,
  filters: Partial<IAgreementLineListFilter>,
  sortOrder?: string
): Promise<IAgreementLineListResult> => {
  const endDateEnd = filters.endDate?.to ? filters.endDate?.to : filters.endDate?.from;
  const lastModifiedEnd = filters.lastModified?.to
    ? filters.lastModified?.to
    : filters.lastModified?.from;
  const enabledInTollDomainsList = filters.enabledInTollDomains
    ?.map((preset) => preset.value)
    .join(',');

  const appliedFilters: IAgreementLineListQueryParams = {
    paymentIdentifier: filters.paymentIdentifier,
    agreementLineId: filters.agreementLineId,
    agreementId: filters.agreementId,
    replacementPaymentIdentifier: filters.replacementPaymentIdentifier,
    replacementAgreementLineId: filters.replacementAgreementLineId,
    type: filters.type,
    licensePlateNumber: filters.licensePlateNumber,
    licensePlateCountryCode: filters.licensePlateCountryCode,
    endDateStart: filters.endDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.endDate?.from)
      : undefined,
    endDateEnd: endDateEnd ? convertCopenhagenDateToUtcISOString(endDateEnd) : undefined,
    invoiceReference: filters.invoiceReference,
    reasonCode: filters.reasonCode,
    status: filters.status,
    enabledInTollDomains:
      (enabledInTollDomainsList?.length ?? 0) > 0 ? enabledInTollDomainsList : undefined,
    lastModifiedStart: filters.lastModified?.from
      ? convertCopenhagenDateToUtcISOString(filters.lastModified?.from)
      : undefined,
    lastModifiedEnd: lastModifiedEnd
      ? convertCopenhagenDateToUtcISOString(lastModifiedEnd)
      : undefined,
  };

  const params: IGetTollDetailsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IAgreementLineListResult>('/v1/agreementLines', {
    params,
  });
  return response.data;
};

export const getAgreementLineDetails = async (agreementLineId: string): Promise<IAgreementLine> => {
  const response = await osbApiAxios.get<IAgreementLine>(`/v1/agreementLine/${agreementLineId}`);
  return response.data;
};

// #endregion Agreement lines

// #region OresundPay Data

export const getOresundPayData = async (): Promise<IOresundPayDataResponse> => {
  const response = await osbApiAxios.get<IOresundPayDataResponse>(`/v1/TollServiceProvider`);
  return response.data;
};

export const putOresundPayData = async (data: IOresundPayData, hash: string) => {
  return osbApiAxios.put<IOresundPayData>(`/v1/TollServiceProvider`, {
    tollServiceProvider: data,
    hash,
  });
};

export const postTollProviderMessage = async (actorCodes: string[]) => {
  return osbApiAxios.post<string>(`/v1/TollServiceProvider/send`, { actorCodes });
};

// #endregion OresundPay Data

// #region AbnormalBehaviorReport Data

interface IGetAbnormalBehaviorReportListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IAbnormalBehaviorReportListFilter {
  senderId: string;
  apduOriginator: string;
  apduDate: DateRange | undefined;
  received: DateRange | undefined;
  abnormalBehaviorReason: string;
}

interface IAbnormalBehaviorReportListQueryParams {
  apduDateStart?: string;
  apduDateEnd?: string;
  receivedStart?: string;
  receivedEnd?: string;
  apduOriginator?: string;
  senderId?: string;
  abnormalBehaviourReason?: string;
}

export const getAbnormalBehaviorReportList = async (
  page: number,
  pageSize: number,
  filters: Partial<IAbnormalBehaviorReportListFilter>,
  sortOrder?: string
): Promise<IAbnormalBehaviorReportDataListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const receivedEnd = filters.received?.to ? filters.received?.to : filters.received?.from;

  const appliedFilters: IAbnormalBehaviorReportListQueryParams = {
    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate?.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,
    receivedStart: filters.received?.from
      ? convertCopenhagenDateToUtcISOString(filters.received?.from)
      : undefined,
    receivedEnd: receivedEnd ? convertCopenhagenDateToUtcISOString(receivedEnd) : undefined,

    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    abnormalBehaviourReason: filters.abnormalBehaviorReason,
  };

  const params: IGetAbnormalBehaviorReportListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };

  const response = await osbApiAxios.get<IAbnormalBehaviorReportDataListResult>(
    '/v1/ReportAbnormalBehaviour',
    {
      params,
    }
  );
  return response.data;
};

export const getAbnormalBehaviorReportFile = async (
  abnormalBehaviorReportId: string
): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/ReportAbnormalBehaviour/${abnormalBehaviorReportId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion AbnormalBehaviorReport Data

// #region Accepted Objects Data

export const getAcceptedObjectsData = async (
  messageType: AcceptedObjectMessageType,
  page: number,
  pageSize: number
): Promise<IAcceptedObjectResponse> => {
  const response = await osbApiAxios.get<IAcceptedObjectResponse>(`/v1/AcceptedObject`, {
    params: { messageType, page, pageSize, sortOrder: 'createdAt|desc' },
  });
  return response.data;
};

export const postAcceptedObjectsData = async (
  aduIdentifier: string,
  senderActorCode: string,
  messageType: AcceptedObjectMessageType
): Promise<IAcceptedObject> => {
  const response = await osbApiAxios.post<IAcceptedObject, { data: IAcceptedObject }>(
    `/v1/AcceptedObject`,
    {
      aduIdentifier: aduIdentifier,
      senderActorCode: senderActorCode,
      messageType,
      createdAt: new Date().toISOString(),
    } satisfies IAcceptedObject
  );
  return response.data;
};

export const deleteAcceptedObjectsData = async (data: IAcceptedObject): Promise<string> => {
  const response = await osbApiAxios.delete<string>(`/v1/AcceptedObject`, {
    data,
  });
  return response.data;
};

// #endregion Accepted Objects Data
